import { defineStore } from 'pinia';
import {
  TNormalizedUnit,
  TUnitDataDictionaryEntry,
  TUnitRegistration,
} from 'types';
import {
  fetchDataDictionary,
  fetchRegistrationUnits,
  fetchUnit,
  fetchUnits,
} from 'src/services/units';
import { UNIT_STATUSES } from 'src/boot/constants';

const UNITS_DATA_DICTIONARY_KEY = 'units-data-dictionary';

type State = {
  units: TNormalizedUnit[];
  dataDictionary: TUnitDataDictionaryEntry[];
  pagination: {
    sortBy: string;
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
    lastPage: number;
    search: string;
    organisationId: number | null;
    regionId: number | null;
    groupId: number | null;
  };
  loading: boolean;
};

export const useUnitsStore = defineStore('units', {
  state: (): State => ({
    units: [],
    dataDictionary: [],
    pagination: {
      sortBy: 'org_display_seq',
      page: 1,
      perPage: 12,
      total: 0,
      totalPages: 0,
      lastPage: 1,
      search: '',
      organisationId: null,
      regionId: null,
      groupId: null,
    },
    loading: false,
  }),
  getters: {
    unitsByRegion:
      (state) =>
      (regionId: number): TUnitDataDictionaryEntry[] => {
        return state.dataDictionary
          .filter((unit) => unit.region_id === regionId)
          .sort((a, b) => a.unit_name.localeCompare(b.unit_name));
      },
    unitsByName:
      (state) =>
      (search: string, regionId?: number): TUnitDataDictionaryEntry[] => {
        const needle = search.toLowerCase();
        return state.dataDictionary
          .filter((unit) => {
            const matchesName = unit.unit_name.toLowerCase().includes(needle);
            const matchesRegion =
              regionId === undefined || unit.region_id === regionId;
            return matchesName && matchesRegion;
          })
          .sort((a, b) => a.unit_name.localeCompare(b.unit_name));
      },
  },
  actions: {
    async getUnit(id: number): Promise<TNormalizedUnit> {
      return await fetchUnit(id);
    },
    async getUnits() {
      this.loading = true;

      try {
        const response = await fetchUnits(this.pagination);
        const data = response.data;
        const paginationData = response.meta.page;

        this.units = data;
        this.pagination = {
          ...this.pagination,
          page: paginationData.current_page,
          perPage: paginationData.per_page,
          total: paginationData.total,
          totalPages: paginationData.total_pages,
          lastPage: paginationData.total_pages,
        };
      } catch {
        // Handle permission errors (403) and other failures gracefully
        this.units = [];
        this.pagination = {
          ...this.pagination,
          page: 1,
          total: 0,
          totalPages: 0,
          lastPage: 1,
        };
      } finally {
        this.loading = false;
      }
    },
    clearUnits() {
      this.units = [];
    },
    clear() {
      this.units = [];
      this.pagination = {
        sortBy: 'org_display_seq',
        page: 1,
        perPage: 12,
        total: 0,
        totalPages: 0,
        lastPage: 1,
        search: '',
        organisationId: null,
        regionId: null,
        groupId: null,
      };
      this.loading = false;
    },
    clearDataDictionary() {
      this.dataDictionary = [];
    },
    async getUnitsForQuery(name: string): Promise<TUnitRegistration[]> {
      const statusIds = [
        UNIT_STATUSES.LODGE_STATUS_NORMAL,
        UNIT_STATUSES.CHAPTER_STATUS_NORMAL,
        UNIT_STATUSES.LODGE_STATUS_NOT_MEETING,
        UNIT_STATUSES.CHAPTER_STATUS_NOT_MEETING,
      ];
      const responseData = await fetchRegistrationUnits({
        name,
        statusIds,
      });
      return responseData.data;
    },

    // Data dictionary methods
    cacheDataDictionary(data: TUnitDataDictionaryEntry[]) {
      if (data) {
        localStorage.setItem(UNITS_DATA_DICTIONARY_KEY, JSON.stringify(data));
      }
    },

    async getDataDictionary() {
      // Load from cache first
      const cachedData = localStorage.getItem(UNITS_DATA_DICTIONARY_KEY);
      if (cachedData) {
        this.dataDictionary = JSON.parse(
          cachedData,
        ) as TUnitDataDictionaryEntry[];

        // Fetch fresh data in background
        void fetchDataDictionary().then((response) => {
          const data = response.data;
          this.dataDictionary = data;
          this.cacheDataDictionary(data);
        });
        return;
      }

      // No cache - fetch and cache
      const response = await fetchDataDictionary();
      const data = response.data as TUnitDataDictionaryEntry[];
      this.dataDictionary = data;
      this.cacheDataDictionary(data);
    },
  },
});
